import PropTypes from 'prop-types'
import { Button } from 'antd'
import { CalendarOutlined, FileSearchOutlined } from '@ant-design/icons'
import { Link } from 'react-router-dom'
import { createUseStyles } from 'react-jss'
import startCase from 'lodash/startCase'
import datetime from '../../lib/datetime'
import { toPath } from '../../routes'

// Width of antd's expand-icon column. The expanded details are padded by the
// same amount so their fields line up with the card above them.
export const EXPAND_COLUMN_WIDTH = 40

const useStyles = createUseStyles({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  name: {
    fontWeight: 600,
    fontSize: 15,
    minWidth: 0,
    overflowWrap: 'anywhere',
    // Let a long patient name wrap instead of overflowing the card.
    '& a': {
      whiteSpace: 'normal !important',
    },
  },
  line: {
    fontSize: 13,
    color: '#595959',
    overflowWrap: 'anywhere',
  },
  lineIcon: {
    marginRight: 6,
    color: '#8c8c8c',
  },
  // Same grid, indented to align with the primary card past the expand column.
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px 16px',
    paddingLeft: EXPAND_COLUMN_WIDTH,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  fieldLabel: {
    fontSize: 11,
    color: '#8c8c8c',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  fieldValue: {
    fontSize: 14,
    color: '#1c1c1c',
    overflowWrap: 'anywhere',
  },
  // Lets a field take the full width of the 2-column details grid.
  spanTwo: {
    gridColumn: '1 / -1',
  },
})

// Shared by the desktop Date column and the mobile card so both keep the exact
// same format. Returns null when there is no date (datetime(undefined) would
// render today), leaving the dash up to the caller.
export function formatNotificationDate(date) {
  if (!date) return null
  return datetime(date).tz().calendar(null, {
    sameElse: 'MMM D, YYYY h:mm A',
  })
}

// Shared by the desktop "Sent by" column and the expanded details.
export function formatAuthorName(author) {
  if (!author) return null
  return (
    startCase([author.firstName, author.lastName].filter(Boolean).join(' ')) ||
    null
  )
}

function Field({ label, children = null, span = false }) {
  const classes = useStyles()
  return (
    <div
      className={span ? `${classes.field} ${classes.spanTwo}` : classes.field}
    >
      <span className={classes.fieldLabel}>{label}</span>
      <span className={classes.fieldValue}>{children}</span>
    </div>
  )
}

Field.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
  span: PropTypes.bool,
}

// Always-visible summary shown for every notification row on mobile.
export function NotificationMobileCard({ notification }) {
  const classes = useStyles()
  const { createdAt, id, notificationType, patient, subType } = notification
  // Line 2: type · sub-type (empties dropped so there's no dangling separator).
  const typeLine = [startCase(notificationType), startCase(subType)]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className={classes.card}>
      <div className={classes.name}>
        {patient ? (
          // Same link as the desktop Patient column.
          <Link to={toPath.patients.patient({ id: patient.id })}>
            {patient.lastName}, {patient.firstName}
          </Link>
        ) : (
          '—'
        )}
      </div>
      {typeLine && <div className={classes.line}>{typeLine}</div>}
      <div className={classes.line}>
        <CalendarOutlined className={classes.lineIcon} />
        {formatNotificationDate(createdAt) || '—'}
      </div>
      <div>
        <Link to={toPath.notifications.notification({ id })}>
          <Button
            type="link"
            size="small"
            icon={<FileSearchOutlined />}
            style={{ paddingLeft: 0 }}
          >
            View notification
          </Button>
        </Link>
      </div>
    </div>
  )
}

NotificationMobileCard.propTypes = {
  notification: PropTypes.shape({
    id: PropTypes.string.isRequired,
    createdAt: PropTypes.string,
    notificationType: PropTypes.string,
    subType: PropTypes.string,
    patient: PropTypes.shape({
      id: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
    }),
  }).isRequired,
}

// Fields hidden behind the per-row expand toggle.
export function NotificationMobileDetails({ notification }) {
  const classes = useStyles()
  const { author, title } = notification

  return (
    <div className={classes.detailsGrid}>
      <Field label="Title" span>
        {title || '—'}
      </Field>
      <Field label="Sent by">{formatAuthorName(author) || '—'}</Field>
    </div>
  )
}

NotificationMobileDetails.propTypes = {
  notification: PropTypes.shape({
    title: PropTypes.string,
    author: PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string,
    }),
  }).isRequired,
}
